<script setup>
    import { ref, watchEffect } from 'vue';
    import { getFromAPI, buildPageFooter, getFullUrlWithFilters, isButtonDisabled, download_file } from '../functionCollection.js';
    import SearchFields from './SearchFields.vue';

    const props = defineProps(['fetchUrl', 'exportUrl', 'actions']);
    const antragList = ref(null);
    const columnsToFetch = ['id', 'rechnungsempfaenger', 're_sb','tarifGroup', 'name', 'anrede', 'name1', 'name2', 'email', 'created_at', 'edit_date', 'activated_date', 'extra','verify_if_suspended'].toString();
    const doNotExportDeleted = ref(false);

    // for the page footer
    const currentPage = ref(1);
    const maxPageNumber = ref(1);
    const antraegeOnPage = ref(null);
    const footerPageNumbers = ref(new Array());

    // for the search fields. index is the column name in kundenantrag
    const filtersAsString = ref('');
    const receivedFilterParameters = ref([]);
    const filterParameters = [
        {index: 'rechnungsempfaenger', title: 'Kundennummer', value: null},
        {index: 're_sb', title: 'SB-Kundennummer', value: null},
        {index: 'tarifGroup', title: 'Tarifgruppe', value: null},
        {index: 'name1', title: 'Nachname', value: null},
        {index: 'name2', title: 'Vorname', value: null},
        {index: 'Email', title: 'E-Mail', value: null},
        {index: 'plz', title: 'PLZ', value: null},
        {index: 'ort', title: 'Ort', value: null},
        {index: 'strasse', title: 'Straße', value: null},
        {index: 'edit_date', title: 'Zuletzt bearbeitet', value: null},
        {index: 'activated_date', title: 'Aktiviert', value: null},
        {index: 'isKiosk', title: 'Am Kiosk erstellt', value: null},
        {index: 'extra', title: 'Dienstausweisnummer', value: null},
    ];

    watchEffect(async () => {
        const url = getFullUrlWithFilters(props.fetchUrl, currentPage.value, columnsToFetch, filtersAsString.value);
        antragList.value = await getFromAPI(url);
        maxPageNumber.value = antragList.value.meta.last_page;
        antragList.value = antragList.value.data;
        antraegeOnPage.value = antragList.value.length;
        footerPageNumbers.value = buildPageFooter(currentPage.value, maxPageNumber.value);
    });

    // calls new suspendedUsers page whenever a page change is requested (i.e. a page button is pressed in the footer)
    async function getNewPage(newPage) {
        currentPage.value = newPage;
        if (currentPage.value < 1) {
            currentPage.value = 1;
        }
        antragList.value = await getFromAPI(getFullUrlWithFilters(props.fetchUrl, currentPage.value, columnsToFetch, filtersAsString.value));
        maxPageNumber.value = antragList.value.meta.last_page;
        antragList.value = antragList.value.data;
        antraegeOnPage.value = antragList.value.length;
        buildPageFooter(currentPage.value, maxPageNumber.value);
    }

    // builds search query URL parameters, removing empty fields from query, and executes the search.
    function filterSearch(givenFields) {
        receivedFilterParameters.value = [];
        for (let i = 0; i < givenFields.length; i++) {
            if (givenFields[i].value !== null) {
                receivedFilterParameters.value.push(givenFields[i]);
            }
            if (givenFields[i].index === 'suspended') {
                if (givenFields[i].value === 'Ja' || givenFields[i].value === 'ja') {
                    givenFields[i].value = 1;
                } else if (givenFields[i].value === 'Nein' || givenFields[i].value === 'nein') {
                    givenFields[i].value = 0;
                }
            }
        }
        filtersAsString.value = JSON.stringify(receivedFilterParameters.value);
        getNewPage(1);
    }

    function replaceIDinUrl(url, id) {
        // needs to remove :id and replace it with the actual ID. Where did I do that before...?
        // let lockUrl = props.lockUrl.replace(':id', mobilityKeyToLock.value.id);
        let formattedUrl = url.replace(':id', id);
        return formattedUrl;
    }

    // gives the parameters so that the backend can actually look up the needed data for the csv.
    function fetchExport() {
        let exportUrl = props.exportUrl;

        //go through all search field filter parameters to see if the list is already smaller than it is.
        let toFilter = [];
        if (receivedFilterParameters.value !== null && receivedFilterParameters.value.length > 0) {
            for (let i = 0; i < receivedFilterParameters.value.length; i++) { //below, value means the key 'value' in the object, not Vue's ref system.
                if (typeof receivedFilterParameters.value[i].value !== 'undefined' && receivedFilterParameters.value[i].value.trim() !== '') {
                    toFilter.push(receivedFilterParameters.value[i]);
                }
            }
        }

        toFilter.push({
            'index': 'removedCustomers',
            'title': 'removedCustomers',
            'value': doNotExportDeleted.value,
        });

        let filterJSON = encodeURIComponent(JSON.stringify(toFilter));
        exportUrl += '?filter=' + filterJSON;

        download_file(exportUrl);
    }

</script>

<template>
    <SearchFields
        :fields="filterParameters"
        @filters="filterSearch"
    />

    <div class="table-header" v-if="antragList && antragList.length > 0">
        <div class="actions">
            <div class="button-container">
                <button class="btn btn-link header-button" @click.prevent="fetchExport()">
                    <i class="fa-solid fa-file-csv fa-lg"></i> Exportieren
                </button>
                <label for="removedCustomers">
                    <input type="checkbox" id="removedCustomers" v-model="doNotExportDeleted" />
                    Gelöschte Kunden nicht exportieren
                </label>
            </div>
        </div>
    </div>

    <div class="main-table" v-if="antragList && antragList.length > 0">
        <div class="inner-table">
            <table class="table">
                <thead>
                    <tr>
                        <th class="table-head">ID</th>
                        <th class="table-head">Kundennr.</th>
                        <th class="table-head">SB-Kundennr.</th>
                        <th class="table-head">Tarifgruppe</th>
                        <th class="table-head">Parkhaus</th>
                        <th class="table-head">Medium</th>
                        <th class="table-head">Nachname</th>
                        <th class="table-head">Vorname</th>
                        <th class="table-head">E-Mail</th>
                        <th class="table-head">Erstellt am</th>
                        <th class="table-head">Bearbeitet</th>
                        <th class="table-head">Aktiviert</th>
                        <th class="table-head">Dienst-ausweisnr.</th>
                        <th class="table-head">Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="antrag of antragList" :key="antrag.id" :class="{'m-datatable__row': true, 'warning': antrag.verify_if_suspended === 1}">
                        <td>{{ antrag.id }}</td>
                        <td>{{ antrag.rechnungsempfaenger }}</td>
                        <td>{{ antrag.re_sb }}</td>
                        <td>{{ antrag.tarifGroup }}</td>
                        <td>{{ antrag.name }}</td>
                        <td>{{ antrag.medium }}</td>
                        <td>{{ antrag.name1 }}</td>
                        <td>{{ antrag.name2 }}</td>
                        <td>{{ antrag.email }}</td>
                        <td>{{ antrag.created_at }}</td>
                        <td>{{ antrag.edit_date }}</td>
                        <td>{{ antrag.activated_date }}</td>
                        <td>{{ antrag.extra }}</td>
                        <td>
                            <div class="buttons">
                                <a  class="btn btn-primary"
                                    :href="replaceIDinUrl(props.actions[0].url, antrag.id)"
                                    role="button">
                                    Bearbeiten
                                </a>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="table-footer middle" v-if="antragList && antragList.length > 0 && footerPageNumbers.length > 1">
        <ul class="pagination">
            <li class="page-item">
                <button class="page-link" :disabled="isButtonDisabled('start')" @click="getNewPage(currentPage - 1)">
                    <i class="fas fa-lg fa-caret-left"></i>
                </button>
            </li>
            <li v-for="page in footerPageNumbers" :key="page.id" :class="{ 'active' : page == currentPage }" >
                <button class="page-link" @click="getNewPage(page)" :disabled="isButtonDisabled(page)">{{ page }}</button>
            </li>
            <li class="page-item">
                <button class="page-link" @click="getNewPage(currentPage + 1)" :disabled="isButtonDisabled('end')">
                    <i class="fas fa-lg fa-caret-right"></i>
                </button>
            </li>
        </ul>
    </div>

    <div class="m-3" v-if="antragList && (antragList.length == 0 || antragList == null)">
        <div class="alert alert-custom" role="alert">
            <i class="fa-solid fa-comment fa-4x"></i>
            <span>Es wurden keine Kundenanträge gefunden.</span>
        </div>
    </div>

</template>
